import React, { useCallback } from 'react';
import { useAppDispatch } from '../../../../redux/hooks';
import { closeModal } from '../../../../redux/slices/modal';
import { setSidebarState } from '../../../../redux/slices/userSettings';
import { SidebarState } from '../../../../redux/slices/userSettings/types';
import KubitModal from '../../../KubitModal';
import { useScratchpadContext } from '../../../Scratchpad/ScratchpadContext';
import AddToScratchpadForm from './AddToScratchpadForm';
import { IAddToScratchpadFormState } from './types';
import { ScratchpadNoteType } from '../../../../utils/apollo/graphqlSchema';
import { isEmpty } from 'lodash';

interface IProps {
  analysisId: string;
}

const AddToScratchpadModal = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();

  const { addNote, visitScratchpad, handleCreateNew } = useScratchpadContext();

  const handleSubmit = useCallback(
    async (formData: IAddToScratchpadFormState) => {
      dispatch(setSidebarState(SidebarState.Scratchpad));

      let targetScratchpadId = formData.scratchpadId;

      // If creating new scratchpad, get the new ID
      if (!isEmpty(formData.name)) {
        const newScratchpadId = await handleCreateNew(formData.name);
        if (newScratchpadId) {
          targetScratchpadId = newScratchpadId;
        }
      }

      // Add the report note to the correct scratchpad
      await addNote({
        scratchpadId: targetScratchpadId,
        noteType: ScratchpadNoteType.Report,
        report: {
          analysisId,
        },
        richText: null,
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        seq: null,
      });

      // Add blank note after the report note
      await addNote({
        scratchpadId: targetScratchpadId,
        noteType: ScratchpadNoteType.Text,
        richText: {
          html: '',
        },
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        seq: null,
        report: null,
      });

      // Visit the scratchpad
      await visitScratchpad(targetScratchpadId);

      dispatch(closeModal());
    },
    [analysisId, dispatch, addNote, visitScratchpad, handleCreateNew],
  );

  const handleReset = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const defaultValues = {
    scratchpadId: '',
    name: '',
    description: '',
  };

  return (
    <KubitModal
      header="Add to Scratchpad"
      open={true}
      onClose={() => dispatch(closeModal())}
    >
      <AddToScratchpadForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onReset={handleReset}
      />
    </KubitModal>
  );
};

export default AddToScratchpadModal;
